import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { isOfficeFile } from "../lib/fileTypes";

import { BrandLoader } from "./BrandLoader";

export type UploadJob = {
  id: string;
  name: string;
  roomId: string;
  folderId: string | null;
  folderLabel: string;
  documentId?: string;
  status: "queued" | "uploading" | "processing" | "done" | "failed";
  progress: number;
  error?: string;
};

type Ctx = {
  jobs: UploadJob[];
  activeCount: number;
  enqueue: (opts: {
    roomId: string;
    folderId?: string | null;
    folderLabel?: string;
    files?: FileList | File[];
    items?: Array<{ file: File; folderId: string | null; folderLabel: string }>;
    onComplete?: () => void;
  }) => void;
  clearCompleted: () => void;
};

const UploadCtx = createContext<Ctx | null>(null);

async function waitForReady(documentId: string, maxMs = 45000): Promise<"ready" | "failed" | "timeout"> {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const st = await api<{ status: string }>(`/api/v1/files/${documentId}/status`);
    if (st.status === "ready") return "ready";
    if (st.status === "failed") return "failed";
    await new Promise((r) => setTimeout(r, 800));
  }
  return "timeout";
}

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<UploadJob[]>([]);

  const runOne = useCallback(
    async (jobId: string, file: File, roomId: string, folderId: string | null, onComplete?: () => void) => {
      try {
        setJobs((j) => j.map((x) => (x.id === jobId ? { ...x, status: "uploading", progress: 8 } : x)));
        const meta = await api<{ documentId: string; uploadPath: string; status?: string }>("/api/v1/files/upload-url", {
          method: "POST",
          body: JSON.stringify({
            roomId,
            folderId,
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
            sizeBytes: file.size,
          }),
        });
        setJobs((j) =>
          j.map((x) => (x.id === jobId ? { ...x, documentId: meta.documentId, progress: 28 } : x)),
        );
        const uploadRes = await api<{ ok: boolean; status: string }>(meta.uploadPath, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        setJobs((j) =>
          j.map((x) =>
            x.id === jobId
              ? {
                  ...x,
                  progress: 72,
                  status: "processing",
                  error: isOfficeFile(file.name, file.type) ? "Converting to PDF for preview…" : undefined,
                }
              : x,
          ),
        );

        let finalStatus = uploadRes.status;
        if (finalStatus !== "ready" && finalStatus !== "failed") {
          const polled = await waitForReady(meta.documentId);
          finalStatus = polled === "timeout" ? "processing" : polled;
        }

        if (finalStatus === "ready") {
          setJobs((j) => j.map((x) => (x.id === jobId ? { ...x, progress: 100, status: "done" } : x)));
          onComplete?.();
        } else if (finalStatus === "failed") {
          setJobs((j) =>
            j.map((x) =>
              x.id === jobId ? { ...x, status: "failed", error: "Processing failed. Try re-uploading." } : x,
            ),
          );
        } else {
          setJobs((j) =>
            j.map((x) =>
              x.id === jobId
                ? { ...x, status: "processing", progress: 90, error: "Still processing. Refresh the file list shortly." }
                : x,
            ),
          );
          onComplete?.();
        }
      } catch (e) {
        setJobs((j) =>
          j.map((x) => (x.id === jobId ? { ...x, status: "failed", error: String((e as Error).message || e) } : x)),
        );
      }
    },
    [],
  );

  const enqueue = useCallback(
    (opts: {
      roomId: string;
      folderId?: string | null;
      folderLabel?: string;
      files?: FileList | File[];
      items?: Array<{ file: File; folderId: string | null; folderLabel: string }>;
      onComplete?: () => void;
    }) => {
      const rows =
        opts.items?.map((item) => ({
          file: item.file,
          folderId: item.folderId,
          folderLabel: item.folderLabel,
        })) ??
        [...(opts.files ?? [])].map((file) => ({
          file,
          folderId: opts.folderId ?? null,
          folderLabel: opts.folderLabel ?? "Space root",
        }));
      if (!rows.length) return;
      const newJobs: UploadJob[] = rows.map((row) => ({
        id: crypto.randomUUID(),
        name: row.file.name,
        roomId: opts.roomId,
        folderId: row.folderId,
        folderLabel: row.folderLabel,
        status: "queued" as const,
        progress: 0,
      }));
      setJobs((j) => [...j, ...newJobs]);

      void (async () => {
        const concurrency = 3;
        let idx = 0;
        const workers = Array.from({ length: concurrency }, async () => {
          while (idx < rows.length) {
            const i = idx++;
            const row = rows[i];
            await runOne(newJobs[i].id, row.file, opts.roomId, row.folderId, opts.onComplete);
          }
        });
        await Promise.all(workers);
      })();
    },
    [runOne],
  );

  const clearCompleted = useCallback(() => {
    setJobs((j) => j.filter((x) => x.status !== "done" && x.status !== "failed"));
  }, []);

  const activeCount = jobs.filter((j) => j.status === "queued" || j.status === "uploading" || j.status === "processing").length;
  const value = useMemo(() => ({ jobs, activeCount, enqueue, clearCompleted }), [jobs, activeCount, enqueue, clearCompleted]);
  return <UploadCtx.Provider value={value}>{children}</UploadCtx.Provider>;
}

export function useUploadQueue() {
  const ctx = useContext(UploadCtx);
  if (!ctx) throw new Error("UploadQueueProvider missing");
  return ctx;
}

/** @deprecated prefer enqueue */
export function useUploadFilesCompat() {
  const { enqueue } = useUploadQueue();
  return {
    uploadFiles: async (roomId: string, folderId: string | null, files: FileList | File[]) => {
      enqueue({ roomId, folderId, folderLabel: folderId ? "Folder" : "All files", files });
    },
  };
}

const STATUS_LABEL: Record<UploadJob["status"], string> = {
  queued: "Queued",
  uploading: "Uploading",
  processing: "Converting…",
  done: "Ready",
  failed: "Failed",
};

export function UploadQueuePanel() {
  const { jobs, clearCompleted } = useUploadQueue();
  const [open, setOpen] = useState(true);
  if (!jobs.length) return null;
  const active = jobs.filter((j) => j.status !== "done" && j.status !== "failed");
  const shown = active.length ? active : jobs.slice(-6);
  const clearable = jobs.filter((j) => j.status === "done" || j.status === "failed").length;
  return (
    <div className={`upload-panel${open ? "" : " is-collapsed"}`} role="status" aria-live="polite">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: open ? 8 : 0 }}>
        <button type="button" className="ghost" onClick={() => setOpen((v) => !v)} style={{ padding: 0 }}>
          <h4 style={{ margin: 0 }}>
            Uploads ({jobs.length})
            {!open && active.length ? ` · ${active.length} active` : ""}
          </h4>
        </button>
        <div className="row" style={{ gap: 4 }}>
          {clearable > 0 ? (
            <button type="button" className="ghost" onClick={clearCompleted}>
              Clear
            </button>
          ) : null}
          <button type="button" className="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "−" : "+"}
          </button>
        </div>
      </div>
      {open &&
        shown.map((j) => (
          <div key={j.id} className="upload-item">
            <div className="row">
              <span className="grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {j.name}
              </span>
              <span className="muted" style={j.status === "failed" ? { color: "#9f1d2e" } : undefined}>
                {STATUS_LABEL[j.status]}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              → {j.folderLabel}
            </div>
            {(j.status === "queued" || j.status === "uploading" || j.status === "processing") ? (
              <div className="upload-item__loader">
                <BrandLoader size="sm" progress={j.progress} />
                <span className="muted" style={{ fontSize: 12 }}>{j.progress}%</span>
              </div>
            ) : null}
            <div
              className="progress"
              role="progressbar"
              aria-valuenow={j.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-hidden={j.status === "queued" || j.status === "uploading" || j.status === "processing"}
            >
              <span style={{ width: `${j.progress}%` }} />
            </div>
            {j.error && (
              <div className="muted" role="alert" style={{ color: "#9f1d2e" }}>
                {j.error}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}
